import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyIcon from "@mui/icons-material/Key";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  generateApiKeyPair,
  listApiKeys,
  revokeApiKey,
  type ApiKey,
  type ApiKeyEnvironment,
  type CreatedApiKeyPair,
} from "../api/apiKeys";
import {
  listInboundEvents,
  listOutboundEvents,
  retryOutboundEvent,
  type AdminEvent,
} from "../api/events";
import { getMerchant, type Merchant } from "../api/merchants";
import { MerchantBillingStatsView } from "../components/BillingStatsView";
import { InvoicesSection } from "../components/InvoicesSection";
import { PricingPlanSection } from "../components/PricingPlanSection";
import { ProviderConfigsSection } from "../components/ProviderConfigsSection";

export function MerchantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const justCreated = search.get("created") === "1";

  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<
    "info" | "activity" | "plan" | "invoices" | "gateways" | "keys" | "events"
  >(justCreated ? "keys" : "info");

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getMerchant(id)
      .then(setMerchant)
      .catch((err) => setError(err instanceof Error ? err.message : "Error"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !merchant) {
    return <Alert severity="error">{error ?? "Comercio no encontrado"}</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/merchants")} size="small">
          Volver
        </Button>
      </Box>

      <Stack direction="row" alignItems="center" spacing={2}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            {merchant.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
            {merchant.id}
          </Typography>
        </Box>
        <Chip
          label={
            merchant.status === "disabled"
              ? "Inactivo"
              : merchant.status === "suspended"
              ? "Suspendido"
              : "Activo"
          }
          color={merchant.status === "disabled" ? "default" : "success"}
          size="small"
        />
      </Stack>

      {justCreated && (
        <Alert severity="success">
          Comercio creado. Generá el par de claves (privada + pública) en la pestaña{" "}
          <strong>Claves API</strong> y entregáselas al comercio — solo se muestran una vez.
        </Alert>
      )}

      <Paper variant="outlined">
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab value="info" label="Datos" />
          <Tab value="activity" label="Actividad" />
          <Tab value="plan" label="Plan" />
          <Tab value="invoices" label="Facturas" />
          <Tab value="gateways" label="Pasarelas" />
          <Tab value="keys" label="Claves API" />
          <Tab value="events" label="Notificaciones" />
        </Tabs>
        <Box sx={{ p: 3 }}>
          {tab === "info" && <InfoSection merchant={merchant} />}
          {tab === "activity" && <MerchantBillingStatsView merchantId={merchant.id} />}
          {tab === "plan" && <PricingPlanSection merchantId={merchant.id} />}
          {tab === "invoices" && <InvoicesSection merchantId={merchant.id} />}
          {tab === "gateways" && <ProviderConfigsSection merchantId={merchant.id} />}
          {tab === "keys" && <ApiKeysSection merchantId={merchant.id} />}
          {tab === "events" && <EventsSection merchantId={merchant.id} />}
        </Box>
      </Paper>
    </Stack>
  );
}

function InfoSection({ merchant }: { merchant: Merchant }) {
  return (
    <Stack spacing={1.5}>
      <Row label="Email de contacto" value={merchant.email} />
      <Row label="Identificador interno" value={merchant.slug} mono />
      <Row label="País principal" value={merchant.defaultCountry} />
      <Row label="Fecha de alta" value={new Date(merchant.createdAt).toLocaleString()} />
      {merchant.updatedAt && (
        <Row label="Última actualización" value={new Date(merchant.updatedAt).toLocaleString()} />
      )}
    </Stack>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 180 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontFamily={mono ? "monospace" : undefined}>
        {value}
      </Typography>
    </Stack>
  );
}

function ApiKeysSection({ merchantId }: { merchantId: string }) {
  const [env, setEnv] = useState<ApiKeyEnvironment>("test");
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [created, setCreated] = useState<CreatedApiKeyPair | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<ApiKey | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setKeys(await listApiKeys(merchantId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "No pudimos cargar las claves.");
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleGeneratePair() {
    setGenerating(true);
    setError(null);
    try {
      const pair = await generateApiKeyPair(merchantId, { environment: env });
      setCreated(pair);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No pudimos generar el par de claves.");
    } finally {
      setGenerating(false);
    }
  }

  async function handleRevoke() {
    if (!pendingRevoke) return;
    try {
      await revokeApiKey(merchantId, pendingRevoke.id);
      setPendingRevoke(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No pudimos revocar la clave.");
    }
  }

  const active = keys.filter((k) => !k.revokedAt && k.environment === env);
  const hasSecret = active.some((k) => k.type === "secret");
  const hasPublishable = active.some((k) => k.type === "publishable");
  const pairReady = hasSecret && hasPublishable;
  const envLabel = env === "test" ? "Pruebas" : "Producción";

  return (
    <Stack spacing={2.5}>
      <Alert severity="info" icon={<KeyIcon />}>
        Para conectar el ERP del comercio necesitás <strong>las dos claves</strong> del mismo
        ambiente: una <strong>privada</strong> (para el servidor) y una <strong>pública</strong>{" "}
        (para el checkout). Acá las generás de una sola vez.
      </Alert>

      <Tabs value={env} onChange={(_, v) => setEnv(v)}>
        <Tab value="test" label="Pruebas" />
        <Tab value="live" label="Producción" />
      </Tabs>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Stack spacing={0.5} sx={{ flex: 1 }}>
            <Typography variant="subtitle2">
              Estado del par de claves · {envLabel}
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <CheckRow ok={hasSecret} label="Clave privada" />
              <CheckRow ok={hasPublishable} label="Clave pública" />
            </Stack>
          </Stack>
          <Button
            variant={pairReady ? "outlined" : "contained"}
            startIcon={<KeyIcon />}
            onClick={handleGeneratePair}
            disabled={generating}
          >
            {generating
              ? "Generando…"
              : pairReady
              ? "Regenerar par"
              : `Generar par (${envLabel})`}
          </Button>
        </Stack>
        {pairReady && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Ya hay un par activo. Si lo regenerás, las claves anteriores quedan en la lista y
            podés revocarlas cuando el comercio termine de migrar.
          </Typography>
        )}
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}

      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            Claves del comercio
          </Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refrescar
          </Button>
        </Stack>

        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tipo</TableCell>
                <TableCell>Ambiente</TableCell>
                <TableCell>Identificador parcial</TableCell>
                <TableCell>Etiqueta</TableCell>
                <TableCell>Creada</TableCell>
                <TableCell>Último uso</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((k) => {
                const revoked = !!k.revokedAt;
                return (
                  <TableRow key={k.id} hover sx={{ opacity: revoked ? 0.55 : 1 }}>
                    <TableCell>
                      {k.type === "secret" ? "Clave privada" : "Clave pública"}
                    </TableCell>
                    <TableCell>{k.environment === "live" ? "Producción" : "Pruebas"}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {k.prefix}…
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={k.label ? undefined : "text.disabled"}>
                        {k.label ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>{new Date(k.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={revoked ? "Revocada" : "Activa"}
                        color={revoked ? "default" : "success"}
                        variant={revoked ? "outlined" : "filled"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {!revoked && (
                        <Tooltip title="Revocar">
                          <IconButton size="small" onClick={() => setPendingRevoke(k)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && keys.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                    Todavía no hay claves. Generá el primer par arriba.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <CircularProgress size={22} />
          </Box>
        )}
      </Stack>

      <NewPairDialog pair={created} onClose={() => setCreated(null)} />

      <Dialog open={!!pendingRevoke} onClose={() => setPendingRevoke(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Revocar clave</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            La clave dejará de funcionar inmediatamente. Si el comercio la está usando en su ERP,
            sus cobros van a fallar hasta que la cambie por una nueva.
          </Typography>
          {pendingRevoke && (
            <Typography variant="body2" sx={{ mt: 1 }} fontFamily="monospace">
              {pendingRevoke.prefix}…
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRevoke(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleRevoke}>
            Revocar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function CheckRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {ok ? (
        <CheckCircleIcon fontSize="small" color="success" />
      ) : (
        <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
      )}
      <Typography variant="body2" color={ok ? "text.primary" : "text.secondary"}>
        {label}
      </Typography>
    </Stack>
  );
}

function NewPairDialog({
  pair,
  onClose,
}: {
  pair: CreatedApiKeyPair | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!pair} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Par de claves generado</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Copiá las dos claves ahora y entregáselas al comercio por un canal seguro.{" "}
          <strong>No se vuelven a mostrar</strong>: si se pierden, hay que generar un par nuevo.
        </Alert>
        {pair?.secret && pair?.publishable && (
          <Stack spacing={2}>
            <SecretField
              label={`Clave PRIVADA · empieza con ${pair.secret.prefix?.slice(0, 3) ?? "sk"}_ · va en el servidor del comercio`}
              value={pair.secret.secret}
            />
            <SecretField
              label={`Clave PÚBLICA · empieza con ${pair.publishable.prefix?.slice(0, 3) ?? "pk"}_ · va en el checkout`}
              value={pair.publishable.secret}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Listo, ya las copié
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SecretField({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        value={value}
        fullWidth
        InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 13 } }}
        label={label}
      />
      <Tooltip title={copied ? "Copiado" : "Copiar"}>
        <IconButton
          onClick={async () => {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function EventsSection({ merchantId }: { merchantId: string }) {
  const [direction, setDirection] = useState<"outbound" | "inbound">("outbound");
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = direction === "outbound"
        ? await listOutboundEvents(merchantId, { limit: 30 })
        : await listInboundEvents(merchantId, { limit: 30 });
      setEvents(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar eventos");
    } finally {
      setLoading(false);
    }
  }, [direction, merchantId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleRetry(eventId: string) {
    try {
      await retryOutboundEvent(merchantId, eventId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al reintentar");
    }
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <TextField
          select
          size="small"
          label="Dirección"
          value={direction}
          onChange={(e) => setDirection(e.target.value as "outbound" | "inbound")}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="outbound">Salientes (hacia el comercio)</MenuItem>
          <MenuItem value="inbound">Entrantes (desde la pasarela)</MenuItem>
        </TextField>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refrescar
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Tipo</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell>Intentos</TableCell>
            <TableCell>Creado</TableCell>
            <TableCell>Último error</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((e) => (
            <TableRow key={e.id} hover>
              <TableCell>
                <Typography variant="body2" fontFamily="monospace">
                  {e.type}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={e.status}
                  color={e.status === "delivered" ? "success" : e.status === "failed" ? "error" : "default"}
                />
              </TableCell>
              <TableCell>{e.attemptCount ?? "—"}</TableCell>
              <TableCell>{new Date(e.createdAt).toLocaleString()}</TableCell>
              <TableCell sx={{ maxWidth: 280 }}>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {e.lastError ?? "—"}
                </Typography>
              </TableCell>
              <TableCell align="right">
                {direction === "outbound" && e.status !== "delivered" && (
                  <Button size="small" onClick={() => handleRetry(e.id)}>
                    Reintentar
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
          {!loading && events.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                Sin eventos por ahora.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      </Box>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress size={22} />
        </Box>
      )}
    </Stack>
  );
}
